import { useMemo } from "react";
import type { ActiveExecution } from "@/stores/companion-store";
import { useCompanionStore } from "@/stores/companion-store";
import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { useAgentStore } from "@/stores/agent-store";
import { useProjectStore } from "@/stores/project-store";
import { agentAccentColor } from "@/lib/agent-colors";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import {
  getExecutionDockCursors,
  getSessionDockCursors,
} from "./agent-avatar-dock-cursors";
import {
  agentNameKey,
  getDockCursors,
  isFailedExecution,
  isReadyExecution,
  isStoppableExecution,
  resolveCursorExecution,
} from "./agent-avatar-dock-utils";

interface AgentDockItem {
  cursor: AgentCursorData;
  execution?: ActiveExecution;
  executionId: string | null;
  avatarSeed: string;
  isError: boolean;
  isRunning: boolean;
  isResultReady: boolean;
  hasUnseenResult: boolean;
  isWaitingApproval: boolean;
}

function itemPriority(item: AgentDockItem): number {
  if (item.isError) return 0;
  if (item.isWaitingApproval) return 1;
  if (item.isRunning) return 2;
  if (item.isResultReady) return 3;
  return 4;
}

function sortAgentDockItems(items: AgentDockItem[]): AgentDockItem[] {
  return [...items].sort((left, right) => {
    const priorityDelta = itemPriority(left) - itemPriority(right);
    if (priorityDelta !== 0) return priorityDelta;
    return (
      new Date(right.cursor.lastSeen).getTime() -
      new Date(left.cursor.lastSeen).getTime()
    );
  });
}

/**
 * Compact-widget rule: the widget shows Denker itself only. Subagent work
 * (native subagents, other agents, orphaned entries without agent identity)
 * streams in the right-edge rail and toast cards, never here. Pure canvas
 * cursors without an execution still pass — they are Denker's own presence.
 */
export function isDenkerDockItem(item: AgentDockItem): boolean {
  const execution = item.execution;
  if (execution?.nativeSubagent) return false;
  if (!execution) return true;
  return (execution.agentKey ?? "").trim().toLowerCase() === "denker";
}

function useAgentDockItems({
  prioritize = false,
  denkerOnly = false,
}: {
  prioritize?: boolean;
  denkerOnly?: boolean;
} = {}): AgentDockItem[] {
  const agentCursors = useCanvasStore((state) => state.agentCursors);
  const executions = useCompanionStore((state) => state.executions);
  const agentSessions = useCompanionStore((state) => state.agentSessions);
  const projectAgents = useAgentStore((state) => state.agents);
  const currentSpaceId = useProjectStore((state) => state.currentSpace?.id);
  const t = useT(cursorsMessages);

  return useMemo(() => {
    const cursorItems = getSessionDockCursors(
      getDockCursors(agentCursors),
      executions,
    );
    const cursors = [
      ...cursorItems,
      ...getExecutionDockCursors(
        cursorItems,
        executions,
        currentSpaceId,
        t("agentFallbackName"),
      ),
    ];
    const avatarSeedsByName = new Map(
      projectAgents.map((agent) => [
        agentNameKey(agent.template_name),
        agent.template_avatar_seed || agent.template_name || "Agent",
      ]),
    );

    const items = cursors.map((sourceCursor) => {
      const cursor = {
        ...sourceCursor,
        color: agentAccentColor(
          sourceCursor.name,
          sourceCursor.sessionId ??
            sourceCursor.executionId ??
            sourceCursor.agentId,
        ),
      };
      const execution = resolveCursorExecution(
        cursor,
        executions,
        agentSessions,
      );
      const isError = !!cursor.error || isFailedExecution(execution);
      const isRunning =
        !isError &&
        (execution
          ? isStoppableExecution(execution)
          : !cursor.frozen && !cursor.error);
      const isResultReady =
        !isError && (!!cursor.frozen || isReadyExecution(execution));
      return {
        cursor,
        execution,
        executionId:
          execution && isStoppableExecution(execution) ? execution.id : null,
        avatarSeed:
          avatarSeedsByName.get(agentNameKey(cursor.name)) ??
          cursor.name ??
          cursor.agentId,
        isError,
        isRunning,
        isResultReady,
        hasUnseenResult:
          isResultReady && cursor.resultAcknowledgedAt !== cursor.lastSeen,
        isWaitingApproval: execution?.status === "waiting_approval",
      };
    });
    const visible = denkerOnly ? items.filter(isDenkerDockItem) : items;
    return prioritize ? sortAgentDockItems(visible) : visible;
  }, [
    agentCursors,
    agentSessions,
    currentSpaceId,
    executions,
    prioritize,
    denkerOnly,
    projectAgents,
    t,
  ]);
}

export { sortAgentDockItems, useAgentDockItems };
export type { AgentDockItem };
